import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import { ClipboardList, RotateCcw, CheckSquare, CheckCircle, Search, X, Edit, Eye, Trash2 } from 'lucide-react';

const BusinessInventories = () => {
  const routeBase = useWorkspaceBasePath();
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadInventories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadInventories = async () => {
    try {
      let url = '/business/inventories';
      if (filter !== 'all') url += `?statut=${filter}`;
      const res = await api.get(url);
      setInventories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ message: 'Supprimer cet inventaire ?', type: 'danger', confirmText: 'Supprimer' })) return;
    try {
      await api.delete(`/business/inventories/${id}`);
      loadInventories();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleCreate = async () => {
    try {
      const res = await api.post('/business/inventories', {});
      // Rediriger vers la page de comptage
      window.location.href = `${routeBase}/inventories/${res.data.id}`;
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const getStatusBadge = (statut) => {
    const styles = {
      en_cours: 'bg-blue-100 text-blue-600',
      termine: 'bg-amber-100 text-amber-600',
      valide: 'bg-green-100 text-green-600'
    };
    const labels = {
      en_cours: 'En cours',
      termine: 'Terminé',
      valide: 'Validé'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[statut] || styles.en_cours}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>;
  }

  // Filtrage par recherche
  const filteredInventories = inventories.filter(inv => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      inv.reference?.toLowerCase().includes(searchLower) ||
      inv.notes?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredInventories.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInventories = filteredInventories.slice(startIndex, startIndex + itemsPerPage);

  // Stats
  const stats = {
    total: inventories.length,
    enCours: inventories.filter(i => i.statut === 'en_cours').length,
    termines: inventories.filter(i => i.statut === 'termine').length,
    valides: inventories.filter(i => i.statut === 'valide').length,
    totalEcarts: inventories.reduce((sum, i) => sum + parseInt(i.total_ecarts || 0), 0)
  };

  return (
    <div className="p-2 sm:p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">Inventaires</h1>
          <p className="text-gray-500 text-xs sm:text-sm">Gérez vos inventaires de stock</p>
        </div>
        <button
          onClick={handleCreate}
          className="px-3 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center justify-center gap-2 text-sm sm:text-base w-full sm:w-auto"
        >
          <ClipboardList size={16} /> Nouvel inventaire
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4 md:gap-4 mb-4 sm:mb-6">
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-gray-500 text-lg sm:text-2xl mb-1 sm:mb-2"><ClipboardList size={24} /></div>
          <div className="text-lg sm:text-2xl font-bold text-gray-800">{stats.total}</div>
          <div className="text-xs sm:text-sm text-gray-500">Total</div>
        </div>
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-blue-500 text-lg sm:text-2xl mb-1 sm:mb-2"><RotateCcw size={24} /></div>
          <div className="text-lg sm:text-2xl font-bold text-blue-600">{stats.enCours}</div>
          <div className="text-xs sm:text-sm text-gray-500">En cours</div>
        </div>
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-amber-500 text-lg sm:text-2xl mb-1 sm:mb-2"><CheckSquare size={24} /></div>
          <div className="text-lg sm:text-2xl font-bold text-amber-600">{stats.termines}</div>
          <div className="text-xs sm:text-sm text-gray-500">À valider</div>
        </div>
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-green-500 text-lg sm:text-2xl mb-1 sm:mb-2"><CheckCircle size={24} /></div>
          <div className="text-lg sm:text-2xl font-bold text-green-600">{stats.valides}</div>
          <div className="text-xs sm:text-sm text-gray-500">Validés</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-2.5 sm:p-4 mb-4 sm:mb-6">
        <div className="mb-3 sm:mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-9 sm:pl-10 pr-8 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Search size={14} /></span>
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={14} /></button>
            )}
          </div>
        </div>
        <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1 -mx-2.5 px-2.5 sm:mx-0 sm:px-0">
          {[
            { value: 'all', label: 'Tous', mobileLabel: 'Tout' },
            { value: 'en_cours', label: <><RotateCcw size={14} className="inline" /> En cours</>, mobileLabel: <RotateCcw size={14} /> },
            { value: 'termine', label: <><CheckSquare size={14} className="inline" /> Terminés</>, mobileLabel: <CheckSquare size={14} /> },
            { value: 'valide', label: <><CheckCircle size={14} className="inline" /> Validés</>, mobileLabel: <CheckCircle size={14} /> }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => { setFilter(f.value); setCurrentPage(1); }}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition flex-shrink-0 ${
                filter === f.value ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="hidden sm:inline">{f.label}</span>
              <span className="sm:hidden">{f.mobileLabel}</span>
            </button>
          ))}
        </div>
      </div>

      {search && (
        <div className="mb-3 sm:mb-4 text-xs sm:text-sm text-gray-500">
          {filteredInventories.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Inventories List */}
      {paginatedInventories.length > 0 ? (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Référence</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Produits</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Écarts</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Valeur écart</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Statut</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {paginatedInventories.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-indigo-600">{inv.reference}</div>
                      {inv.notes && <div className="text-xs text-gray-400 truncate max-w-[200px]">{inv.notes}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(inv.date_inventaire).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-gray-800">
                      {inv.total_produits}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {parseInt(inv.total_ecarts) > 0 ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                          {inv.total_ecarts} écart(s)
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      {parseFloat(inv.valeur_ecart || 0) !== 0 ? (
                        <span className={parseFloat(inv.valeur_ecart) < 0 ? 'text-red-600 font-bold' : 'text-green-600 font-bold'}>
                          {parseFloat(inv.valeur_ecart).toLocaleString()} XOF
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(inv.statut)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-2">
                        <Link
                          to={`${routeBase}/inventories/${inv.id}`}
                          className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition"
                          title={inv.statut === 'en_cours' ? 'Compter' : 'Voir'}
                        >
                          {inv.statut === 'en_cours' ? <Edit size={16} /> : <Eye size={16} />}
                        </Link>
                        {inv.statut !== 'valide' && (
                          <button
                            onClick={() => handleDelete(inv.id)}
                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-2.5">
            {paginatedInventories.map(inv => (
              <div key={inv.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-indigo-600 text-sm">{inv.reference}</div>
                      {inv.notes && <div className="text-xs text-gray-500 truncate">{inv.notes}</div>}
                    </div>
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                      {getStatusBadge(inv.statut)}
                      <span className="text-xs text-gray-400">{new Date(inv.date_inventaire).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <div className="text-xs text-gray-500">Produits</div>
                      <div className="text-sm font-bold text-gray-900">{inv.total_produits}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Écarts</div>
                      <div className={`text-sm font-bold ${parseInt(inv.total_ecarts) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {inv.total_ecarts || 0}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Valeur écart</div>
                      {parseFloat(inv.valeur_ecart || 0) !== 0 ? (
                        <div className={`text-sm font-bold ${parseFloat(inv.valeur_ecart) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {parseFloat(inv.valeur_ecart).toLocaleString()} <span className="text-xs font-normal">XOF</span>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400">-</div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <Link
                      to={`${routeBase}/inventories/${inv.id}`}
                      className="flex-1 min-w-[60px] py-2 px-2 bg-indigo-50 text-indigo-600 rounded-lg text-center font-medium text-xs hover:bg-indigo-100 transition"
                    >
                      {inv.statut === 'en_cours' ? <><Edit size={16} className="inline" /> Compter</> : <><Eye size={16} className="inline" /> Voir</>}
                    </Link>
                    {inv.statut !== 'valide' && (
                      <button
                        onClick={() => handleDelete(inv.id)}
                        className="py-2 px-3 bg-red-50 text-red-600 rounded-lg font-medium text-xs hover:bg-red-100 transition"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-3 sm:mt-0 px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border-t rounded-b-xl flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3">
              <div className="text-xs sm:text-sm text-gray-500">
                {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredInventories.length)} sur {filteredInventories.length}
              </div>
              <div className="flex gap-0.5 sm:gap-1 flex-wrap justify-center">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm">««</button>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm">«</button>
                {(() => {
                  const pages = [];
                  const showPages = new Set();
                  showPages.add(1);
                  showPages.add(totalPages);
                  for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) showPages.add(i);
                  const sortedPages = [...showPages].sort((a, b) => a - b);
                  sortedPages.forEach((page, idx) => {
                    if (idx > 0 && page - sortedPages[idx - 1] > 1) pages.push(<span key={`e-${page}`} className="px-1 sm:px-2 text-xs sm:text-sm text-gray-400">...</span>);
                    pages.push(<button key={page} onClick={() => setCurrentPage(page)} className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>{page}</button>);
                  });
                  return pages;
                })()}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm">»</button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm">»»</button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <span className="text-6xl mb-4 block"><ClipboardList size={40} /></span>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {search ? 'Aucun résultat' : 'Aucun inventaire'}
          </h3>
          <p className="text-gray-500 mb-4">
            {search ? 'Essayez une autre recherche' : 'Lancez votre premier inventaire de stock'}
          </p>
          {!search && (
            <button
              onClick={handleCreate}
              className="inline-block px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
            >
              Nouvel inventaire
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessInventories;
